import { Helmet } from "react-helmet";
import { useEffect, useState } from "react";
import AnalogClock from "../components/zeit/AnalogClock";
import zeitLogo from "../assets/zeit/zeit_logo_256.ico";

// Clock styles to cycle through
const CLOCK_STYLES = [
  { style: "swiss" as const, name: "Swiss" },
  { style: "bauhaus" as const, name: "Bauhaus" },
  { style: "minimal" as const, name: "Minimal" },
  { style: "ticks" as const, name: "Classic" },
] as const;

// SEO metadata
const seoMetadata = {
  title: "ZeitTab - Beautiful New Tab Clock Extension",
  description:
    "Transform your new tab page into a beautiful, customizable clock display. ZeitTab helps you stay focused and organized with elegant time visualization.",
  keywords: "new tab clock, browser extension, productivity tool, custom clock, tab page, time management",
  ogImage: `https://zeit.mokachika.com${zeitLogo}`,
  url: "https://zeit.mokachika.com",
  imageWidth: "256",
  imageHeight: "256",
  imageType: "image/x-icon",
};

// Chrome icon component
const ChromeIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5">
    <path d="M12 0C8.21 0 4.831 1.757 2.632 4.501l3.953 6.848A5.454 5.454 0 0 1 12 6.545h10.691A12 12 0 0 0 12 0zM1.931 5.47A11.943 11.943 0 0 0 0 12c0 6.012 4.42 10.991 10.189 11.864l3.953-6.847a5.45 5.45 0 0 1-6.865-2.29zm13.342 2.166a5.446 5.446 0 0 1 1.45 7.09l.002.003h-.002l-5.344 9.257c.206.01.413.016.621.016 6.627 0 12-5.373 12-12 0-1.54-.29-3.011-.818-4.366zM12 16.364a4.364 4.364 0 1 1 0-8.728 4.364 4.364 0 0 1 0 8.728z" />
  </svg>
);

// Firefox icon component with full gradients
const FirefoxIcon = () => (
  <svg
    className="w-5 h-5"
    viewBox="0 0 24 28"
    version="1.1"
    xmlns="http://www.w3.org/2000/svg"
    xmlnsXlink="http://www.w3.org/1999/xlink"
    xmlSpace="preserve"
    style={{
      fillRule: "evenodd",
      clipRule: "evenodd",
      strokeLinejoin: "round",
      strokeMiterlimit: 2,
    }}
  >
    <g id="logo" transform="matrix(0.045,0,0,0.045,2,2)">
      <path
        id="shape-base"
        d="M478.711,166.353C468.266,141.229 447.111,114.105 430.499,105.532C444.019,132.037 451.844,158.625 454.834,178.468C454.834,178.507 454.849,178.604 454.881,178.868C427.706,111.135 381.627,83.823 344,24.355C342.1,21.348 340.195,18.333 338.339,15.155C337.394,13.532 336.511,11.873 335.693,10.183C334.13,7.165 332.924,3.975 332.1,0.677C332.112,0.355 331.873,0.074 331.554,0.033C331.407,-0.009 331.25,-0.009 331.103,0.033C331.069,0.045 331.019,0.084 330.983,0.098C330.93,0.119 330.863,0.167 330.807,0.198C330.834,0.162 330.89,0.081 330.907,0.062C270.537,35.418 250.057,100.823 248.175,133.546C224.071,135.203 201.016,144.087 182.033,159.034C180.045,157.355 177.967,155.786 175.808,154.334C170.331,135.171 170.098,114.885 175.134,95.602C150.446,106.843 131.244,124.612 117.284,140.302L117.173,140.302C107.646,128.235 108.318,88.429 108.861,80.118C108.747,79.603 101.754,83.748 100.838,84.373C92.431,90.374 84.572,97.107 77.352,104.493C69.136,112.825 61.63,121.827 54.91,131.406C54.91,131.418 54.903,131.432 54.899,131.444C54.899,131.431 54.906,131.418 54.91,131.406C39.457,153.305 28.496,178.051 22.663,204.211C22.548,204.732 22.451,205.272 22.339,205.797C21.887,207.913 20.259,218.497 19.974,220.797C19.952,220.974 19.942,221.144 19.921,221.321C17.816,232.264 16.513,243.346 16.021,254.478C16.021,254.888 15.996,255.294 15.996,255.705C16,388.418 123.6,496 256.324,496C375.189,496 473.884,409.712 493.206,296.37C493.613,293.294 493.939,290.202 494.298,287.099C499.075,245.889 493.768,202.574 478.711,166.353ZM201.716,354.447C202.84,354.984 203.896,355.571 205.05,356.086C205.098,356.119 205.164,356.156 205.213,356.186C204.038,355.624 202.873,355.045 201.716,354.447ZM454.9,178.927L454.868,178.694C454.88,178.779 454.895,178.868 454.908,178.953L454.9,178.927Z"
        style={{ fill: "url(#_Linear1)", fillRule: "nonzero" }}
      />
      <path
        id="body-outer-ring"
        d="M478.711,166.353C468.266,141.229 447.111,114.105 430.499,105.532C444.019,132.037 451.844,158.625 454.834,178.468C454.834,178.41 454.845,178.516 454.87,178.694C454.882,178.779 454.897,178.868 454.91,178.953C477.585,240.423 465.232,302.931 447.431,341.128C419.892,400.228 353.216,460.798 248.855,457.844C136.1,454.651 36.766,370.988 18.223,261.41C14.844,244.13 18.223,235.356 19.923,221.326C17.852,232.142 17.063,235.266 16.023,254.483C16.023,254.893 15.998,255.299 15.998,255.71C16,388.418 123.6,496 256.324,496C375.189,496 473.884,409.712 493.206,296.37C493.613,293.294 493.939,290.202 494.298,287.099C499.075,245.889 493.768,202.574 478.711,166.353Z"
        style={{ fill: "url(#_Radial2)", fillRule: "nonzero" }}
      />
      <path
        id="body-outer-ring-shadow"
        d="M478.711,166.353C468.266,141.229 447.111,114.105 430.499,105.532C444.019,132.037 451.844,158.625 454.834,178.468C454.834,178.41 454.845,178.516 454.87,178.694C454.882,178.779 454.897,178.868 454.91,178.953C477.585,240.423 465.232,302.931 447.431,341.128C419.892,400.228 353.216,460.798 248.855,457.844C136.1,454.651 36.766,370.988 18.223,261.41C14.844,244.13 18.223,235.356 19.923,221.326C17.852,232.142 17.063,235.266 16.023,254.483C16.023,254.893 15.998,255.299 15.998,255.71C16,388.418 123.6,496 256.324,496C375.189,496 473.884,409.712 493.206,296.37C493.613,293.294 493.939,290.202 494.298,287.099C499.075,245.889 493.768,202.574 478.711,166.353Z"
        style={{ fill: "url(#_Radial3)", fillRule: "nonzero" }}
      />
      <path
        id="tail-tip"
        d="M361.922,194.6C362.446,194.968 362.922,195.334 363.415,195.7C357.384,184.996 349.873,175.195 341.105,166.588C266.4,91.892 321.516,4.626 330.811,0.194C330.838,0.158 330.894,0.077 330.911,0.058C270.541,35.414 250.061,100.819 248.179,133.542C250.979,133.348 253.771,133.113 256.621,133.113C301.672,133.113 340.91,157.883 361.922,194.6Z"
        style={{ fill: "url(#_Radial4)", fillRule: "nonzero" }}
      />
      <path
        id="globe-base"
        d="M256.772,209.514C256.379,215.492 235.258,236.107 227.872,236.107C159.533,236.107 148.44,277.442 148.44,277.442C151.467,312.252 175.701,340.917 205.051,356.085C206.39,356.777 207.745,357.402 209.101,358.02C211.426,359.049 213.78,360.012 216.16,360.906C226.223,364.469 236.764,366.502 247.431,366.937C367.211,372.555 390.417,223.743 303.976,180.529C326.113,176.679 349.091,185.582 361.923,194.596C340.911,157.882 301.673,133.112 256.623,133.112C253.773,133.112 250.982,133.347 248.181,133.541C224.077,135.198 201.022,144.082 182.039,159.029C185.703,162.129 189.839,166.273 198.553,174.857C214.855,190.924 256.683,207.562 256.772,209.514Z"
        style={{ fill: "url(#_Radial5)", fillRule: "nonzero" }}
      />
      <path
        id="globe-shadow"
        d="M256.772,209.514C256.379,215.492 235.258,236.107 227.872,236.107C159.533,236.107 148.44,277.442 148.44,277.442C151.467,312.252 175.701,340.917 205.051,356.085C206.39,356.777 207.745,357.402 209.101,358.02C211.426,359.049 213.78,360.012 216.16,360.906C226.223,364.469 236.764,366.502 247.431,366.937C367.211,372.555 390.417,223.743 303.976,180.529C326.113,176.679 349.091,185.582 361.923,194.596C340.911,157.882 301.673,133.112 256.623,133.112C253.773,133.112 250.982,133.347 248.181,133.541C224.077,135.198 201.022,144.082 182.039,159.029C185.703,162.129 189.839,166.273 198.553,174.857C214.855,190.924 256.683,207.562 256.772,209.514Z"
        style={{ fill: "url(#_Radial6)", fillRule: "nonzero" }}
      />
      <path
        id="ear"
        d="M170.829,151.036C172.503,152.116 174.163,153.216 175.81,154.336C170.333,135.173 170.1,114.887 175.136,95.604C150.448,106.845 131.246,124.614 117.286,140.304C118.441,140.271 153.3,139.644 170.829,151.036Z"
        style={{ fill: "url(#_Radial7)", fillRule: "nonzero" }}
      />
      <path
        id="body"
        d="M18.223,261.41C36.766,370.988 136.1,454.651 248.855,457.844C353.216,460.798 419.892,400.224 447.431,341.128C465.231,302.928 477.585,240.428 454.91,178.953L454.902,178.927L454.87,178.694C454.845,178.516 454.83,178.41 454.834,178.468C454.834,178.507 454.849,178.604 454.881,178.868C463.405,234.529 435.091,288.452 390.83,324.912L390.697,325.225C304.452,395.448 221.923,367.593 205.213,356.191C204.039,355.626 202.872,355.045 201.713,354.448C151.431,330.419 130.659,284.61 135.113,245.324C92.656,245.324 78.179,209.515 78.179,209.515C78.179,209.515 116.298,182.336 166.537,205.974C213.067,227.867 256.765,209.517 256.77,209.515C256.681,207.563 214.853,190.925 198.547,174.859C189.834,166.275 185.697,162.136 182.033,159.031C180.045,157.352 177.967,155.783 175.808,154.331C174.159,153.213 172.499,152.113 170.827,151.031C153.299,139.639 118.439,140.266 117.284,140.296L117.173,140.296C107.646,128.229 108.318,88.423 108.861,80.112C108.747,79.597 101.754,83.742 100.838,84.367C92.431,90.368 84.572,97.101 77.352,104.487C69.136,112.821 61.629,121.825 54.91,131.406C54.91,131.418 54.903,131.432 54.899,131.444C54.899,131.431 54.906,131.418 54.91,131.406C39.457,153.305 28.496,178.051 22.663,204.211C22.548,204.732 14.013,242.053 18.223,261.41Z"
        style={{ fill: "url(#_Radial8)", fillRule: "nonzero" }}
      />
      <path
        id="tail-flame"
        d="M341.105,166.587C349.873,175.194 357.384,184.995 363.415,195.699C364.738,196.693 365.974,197.684 367.023,198.651C421.505,248.851 392.959,319.851 390.83,324.911C435.091,288.451 463.405,234.528 454.881,178.867C427.706,111.135 381.627,83.823 344,24.355C342.1,21.348 340.195,18.333 338.339,15.155C337.394,13.532 336.511,11.873 335.693,10.183C334.13,7.165 332.924,3.975 332.1,0.677C332.112,0.355 331.873,0.074 331.554,0.033C331.407,-0.009 331.25,-0.009 331.103,0.033C331.069,0.045 331.019,0.084 330.983,0.098C330.93,0.119 330.863,0.167 330.807,0.198C321.516,4.626 266.4,91.892 341.105,166.587Z"
        style={{ fill: "url(#_Radial9)", fillRule: "nonzero" }}
      />
      <path
        id="tail-small"
        d="M367.023,198.651C365.974,197.684 364.738,196.693 363.415,195.699C362.926,195.331 362.446,194.965 361.922,194.599C349.09,185.585 326.112,176.682 303.975,180.532C390.416,223.746 367.21,372.558 247.43,366.94C236.763,366.505 226.222,364.472 216.159,360.909C213.779,360.014 211.425,359.052 209.1,358.023C207.744,357.405 206.389,356.78 205.05,356.088C205.098,356.121 205.164,356.158 205.213,356.188C221.923,367.588 304.452,395.445 390.697,325.222L390.83,324.909C392.959,319.855 421.505,248.852 367.023,198.651Z"
        style={{ fill: "url(#_Radial10)", fillRule: "nonzero" }}
      />
      <path
        id="cheek-tufts"
        d="M148.439,277.443C148.439,277.443 159.532,236.108 227.871,236.108C235.259,236.108 256.38,215.493 256.771,209.515C257.162,203.537 213.071,227.867 166.538,205.974C116.299,182.336 78.18,209.515 78.18,209.515C78.18,209.515 92.657,245.324 135.114,245.324C130.661,284.61 151.433,330.424 201.714,354.448C202.838,354.985 203.894,355.572 205.048,356.087C175.7,340.918 151.466,312.253 148.439,277.443Z"
        style={{ fill: "url(#_Radial11)", fillRule: "nonzero" }}
      />
      <path
        id="overlay-yellow"
        d="M478.711,166.353C468.266,141.229 447.111,114.105 430.499,105.532C444.019,132.037 451.844,158.625 454.834,178.468C454.834,178.507 454.849,178.604 454.881,178.868C427.706,111.135 381.627,83.823 344,24.355C342.1,21.348 340.195,18.333 338.339,15.155C337.394,13.532 336.511,11.873 335.693,10.183C334.13,7.165 332.924,3.975 332.1,0.677C332.112,0.355 331.873,0.074 331.554,0.033C331.407,-0.009 331.25,-0.009 331.103,0.033C331.069,0.045 331.019,0.084 330.983,0.098C330.93,0.119 330.863,0.167 330.807,0.198C330.834,0.162 330.89,0.081 330.907,0.062C270.537,35.418 250.057,100.823 248.175,133.546C250.975,133.352 253.767,133.117 256.617,133.117C301.67,133.117 340.908,157.887 361.917,194.601C349.085,185.587 326.107,176.684 303.97,180.534C390.411,223.748 367.205,372.56 247.425,366.942C236.758,366.507 226.217,364.474 216.154,360.911C213.774,360.016 211.42,359.054 209.095,358.025C207.739,357.407 206.384,356.782 205.045,356.09C205.093,356.123 205.159,356.16 205.208,356.19C204.034,355.625 202.867,355.044 201.708,354.447C202.832,354.984 203.888,355.571 205.042,356.086C175.692,340.918 151.458,312.253 148.431,277.443C148.431,277.443 159.524,236.108 227.863,236.108C235.251,236.108 256.372,215.493 256.763,209.515C256.674,207.563 214.846,190.925 198.54,174.859C189.827,166.275 185.69,162.136 182.026,159.031C180.038,157.352 177.96,155.783 175.801,154.331C170.324,135.168 170.091,114.882 175.127,95.599C150.439,106.84 131.237,124.609 117.277,140.299L117.166,140.299C107.639,128.232 108.311,88.426 108.854,80.115C108.74,79.6 101.747,83.745 100.831,84.37C92.424,90.371 84.565,97.104 77.345,104.49C69.132,112.823 61.627,121.826 54.91,131.406C54.91,131.418 54.903,131.432 54.899,131.444C54.899,131.431 54.906,131.418 54.91,131.406C39.457,153.305 28.496,178.051 22.663,204.211C22.548,204.732 22.451,205.272 22.339,205.797C21.887,207.913 19.853,218.65 19.569,220.953C19.547,221.13 19.59,220.777 19.569,220.953C17.714,232.048 16.531,243.245 16.025,254.483C16.025,254.893 16,255.299 16,255.71C16,388.418 123.6,496 256.324,496C375.189,496 473.884,409.712 493.206,296.37C493.613,293.294 493.939,290.202 494.298,287.099C499.075,245.889 493.768,202.574 478.711,166.353ZM454.87,178.694C454.882,178.779 454.897,178.868 454.91,178.953L454.902,178.927L454.87,178.694Z"
        style={{ fill: "url(#_Linear12)", fillRule: "nonzero" }}
      />
    </g>
    <defs>
      <linearGradient id="_Linear1" x1="0" y1="0" x2="1" y2="0" gradientUnits="userSpaceOnUse" gradientTransform="matrix(-373.683,384.895,-384.895,-373.683,434.799,76.8811)">
        <stop offset="0" style={{ stopColor: "rgb(255,244,79)", stopOpacity: 1 }} />
        <stop offset="0.05" style={{ stopColor: "rgb(255,244,79)", stopOpacity: 1 }} />
        <stop offset="0.37" style={{ stopColor: "rgb(255,152,14)", stopOpacity: 1 }} />
        <stop offset="0.53" style={{ stopColor: "rgb(255,54,71)", stopOpacity: 1 }} />
        <stop offset="0.7" style={{ stopColor: "rgb(227,21,135)", stopOpacity: 1 }} />
        <stop offset="1" style={{ stopColor: "rgb(227,21,135)", stopOpacity: 1 }} />
      </linearGradient>
      <radialGradient id="_Radial2" cx="0" cy="0" r="1" gradientUnits="userSpaceOnUse" gradientTransform="matrix(614.402,0,0,614.402,436.393,55.1616)">
        <stop offset="0" style={{ stopColor: "rgb(255,189,79)", stopOpacity: 1 }} />
        <stop offset="0.13" style={{ stopColor: "rgb(255,189,79)", stopOpacity: 1 }} />
        <stop offset="0.28" style={{ stopColor: "rgb(255,152,14)", stopOpacity: 1 }} />
        <stop offset="0.47" style={{ stopColor: "rgb(255,55,80)", stopOpacity: 1 }} />
        <stop offset="0.78" style={{ stopColor: "rgb(235,8,120)", stopOpacity: 1 }} />
        <stop offset="0.86" style={{ stopColor: "rgb(229,0,128)", stopOpacity: 1 }} />
        <stop offset="1" style={{ stopColor: "rgb(229,0,128)", stopOpacity: 1 }} />
      </radialGradient>
      <radialGradient id="_Radial3" cx="0" cy="0" r="1" gradientUnits="userSpaceOnUse" gradientTransform="matrix(614.402,0,0,614.402,251.689,262.119)">
        <stop offset="0" style={{ stopColor: "rgb(150,14,24)", stopOpacity: 1 }} />
        <stop offset="0.3" style={{ stopColor: "rgb(150,14,24)", stopOpacity: 1 }} />
        <stop offset="0.35" style={{ stopColor: "rgb(177,25,39)", stopOpacity: 0.74 }} />
        <stop offset="0.43" style={{ stopColor: "rgb(219,41,61)", stopOpacity: 0.34 }} />
        <stop offset="0.5" style={{ stopColor: "rgb(245,51,75)", stopOpacity: 0.09 }} />
        <stop offset="0.53" style={{ stopColor: "rgb(255,55,80)", stopOpacity: 0 }} />
        <stop offset="1" style={{ stopColor: "rgb(255,55,80)", stopOpacity: 0 }} />
      </radialGradient>
      <radialGradient id="_Radial4" cx="0" cy="0" r="1" gradientUnits="userSpaceOnUse" gradientTransform="matrix(273.899,0,0,273.899,303.492,-23.419)">
        <stop offset="0" style={{ stopColor: "rgb(255,244,79)", stopOpacity: 1 }} />
        <stop offset="0.13" style={{ stopColor: "rgb(255,244,79)", stopOpacity: 1 }} />
        <stop offset="0.53" style={{ stopColor: "rgb(255,152,14)", stopOpacity: 1 }} />
        <stop offset="1" style={{ stopColor: "rgb(255,152,14)", stopOpacity: 1 }} />
      </radialGradient>
      <radialGradient id="_Radial5" cx="0" cy="0" r="1" gradientUnits="userSpaceOnUse" gradientTransform="matrix(233.98,0,0,233.98,197.029,390.747)">
        <stop offset="0" style={{ stopColor: "rgb(58,142,230)", stopOpacity: 1 }} />
        <stop offset="0.35" style={{ stopColor: "rgb(58,142,230)", stopOpacity: 1 }} />
        <stop offset="0.67" style={{ stopColor: "rgb(144,89,255)", stopOpacity: 1 }} />
        <stop offset="1" style={{ stopColor: "rgb(193,57,230)", stopOpacity: 1 }} />
      </radialGradient>
      <radialGradient id="_Radial6" cx="0" cy="0" r="1" gradientUnits="userSpaceOnUse" gradientTransform="matrix(124.243,0,0,138.048,233.506,210.325)">
        <stop offset="0" style={{ stopColor: "rgb(144,89,255)", stopOpacity: 0 }} />
        <stop offset="0.21" style={{ stopColor: "rgb(144,89,255)", stopOpacity: 0 }} />
        <stop offset="0.97" style={{ stopColor: "rgb(110,0,139)", stopOpacity: 0.6 }} />
        <stop offset="1" style={{ stopColor: "rgb(110,0,139)", stopOpacity: 0.6 }} />
      </radialGradient>
      <radialGradient id="_Radial7" cx="0" cy="0" r="1" gradientUnits="userSpaceOnUse" gradientTransform="matrix(170.323,0,0,170.323,240.186,36.872)">
        <stop offset="0" style={{ stopColor: "rgb(255,226,38)", stopOpacity: 1 }} />
        <stop offset="0.1" style={{ stopColor: "rgb(255,226,38)", stopOpacity: 1 }} />
        <stop offset="0.79" style={{ stopColor: "rgb(255,113,57)", stopOpacity: 1 }} />
        <stop offset="1" style={{ stopColor: "rgb(255,113,57)", stopOpacity: 1 }} />
      </radialGradient>
      <radialGradient id="_Radial8" cx="0" cy="0" r="1" gradientUnits="userSpaceOnUse" gradientTransform="matrix(810.278,0,0,810.278,395.187,-74.863)">
        <stop offset="0" style={{ stopColor: "rgb(255,244,79)", stopOpacity: 1 }} />
        <stop offset="0.11" style={{ stopColor: "rgb(255,244,79)", stopOpacity: 1 }} />
        <stop offset="0.46" style={{ stopColor: "rgb(255,152,14)", stopOpacity: 1 }} />
        <stop offset="0.72" style={{ stopColor: "rgb(255,54,71)", stopOpacity: 1 }} />
        <stop offset="0.9" style={{ stopColor: "rgb(227,21,135)", stopOpacity: 1 }} />
        <stop offset="1" style={{ stopColor: "rgb(227,21,135)", stopOpacity: 1 }} />
      </radialGradient>
      <radialGradient id="_Radial9" cx="0" cy="0" r="1" gradientUnits="userSpaceOnUse" gradientTransform="matrix(1133.29,0,0,1133.29,327.452,-60.6219)">
        <stop offset="0" style={{ stopColor: "rgb(255,244,79)", stopOpacity: 1 }} />
        <stop offset="0.3" style={{ stopColor: "rgb(255,152,14)", stopOpacity: 1 }} />
        <stop offset="0.57" style={{ stopColor: "rgb(255,54,71)", stopOpacity: 1 }} />
        <stop offset="0.74" style={{ stopColor: "rgb(227,21,135)", stopOpacity: 1 }} />
        <stop offset="1" style={{ stopColor: "rgb(227,21,135)", stopOpacity: 1 }} />
      </radialGradient>
      <radialGradient id="_Radial10" cx="0" cy="0" r="1" gradientUnits="userSpaceOnUse" gradientTransform="matrix(465.218,0,0,465.218,241.932,98.1327)">
        <stop offset="0" style={{ stopColor: "rgb(255,244,79)", stopOpacity: 1 }} />
        <stop offset="0.14" style={{ stopColor: "rgb(255,244,79)", stopOpacity: 1 }} />
        <stop offset="0.48" style={{ stopColor: "rgb(255,152,14)", stopOpacity: 1 }} />
        <stop offset="0.66" style={{ stopColor: "rgb(255,54,71)", stopOpacity: 1 }} />
        <stop offset="0.9" style={{ stopColor: "rgb(227,21,135)", stopOpacity: 1 }} />
        <stop offset="1" style={{ stopColor: "rgb(227,21,135)", stopOpacity: 1 }} />
      </radialGradient>
      <radialGradient id="_Radial11" cx="0" cy="0" r="1" gradientUnits="userSpaceOnUse" gradientTransform="matrix(559.177,0,0,559.177,362.68,124.863)">
        <stop offset="0" style={{ stopColor: "rgb(255,244,79)", stopOpacity: 1 }} />
        <stop offset="0.09" style={{ stopColor: "rgb(255,244,79)", stopOpacity: 1 }} />
        <stop offset="0.63" style={{ stopColor: "rgb(255,152,14)", stopOpacity: 1 }} />
        <stop offset="1" style={{ stopColor: "rgb(255,152,14)", stopOpacity: 1 }} />
      </radialGradient>
      <linearGradient id="_Linear12" x1="0" y1="0" x2="1" y2="0" gradientUnits="userSpaceOnUse" gradientTransform="matrix(-297.6,347.199,-347.199,-297.6,400,69.4411)">
        <stop offset="0" style={{ stopColor: "rgb(255,244,79)", stopOpacity: 0.8 }} />
        <stop offset="0.17" style={{ stopColor: "rgb(255,244,79)", stopOpacity: 0.8 }} />
        <stop offset="0.6" style={{ stopColor: "rgb(255,244,79)", stopOpacity: 0 }} />
        <stop offset="1" style={{ stopColor: "rgb(255,244,79)", stopOpacity: 0 }} />
      </linearGradient>
    </defs>
  </svg>
);

export const ZeitPage = () => {
  const [currentStyleIndex, setCurrentStyleIndex] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(false);

  // Cycle through clock styles
  useEffect(() => {
    const interval = setInterval(() => {
      setIsTransitioning(true);
      setTimeout(() => {
        setCurrentStyleIndex((prev) => (prev + 1) % CLOCK_STYLES.length);
        setIsTransitioning(false);
      }, 400);
    }, 4000);
    return () => clearInterval(interval);
  }, []);

  const currentStyle = CLOCK_STYLES[currentStyleIndex];

  return (
    <>
      <Helmet>
        <title>{seoMetadata.title}</title>
        <meta name="description" content={seoMetadata.description} />
        <meta name="keywords" content={seoMetadata.keywords} />
        <meta name="robots" content="index, follow" />
        <meta name="viewport" content="width=device-width, initial-scale=1.0" />
        <link rel="canonical" href={seoMetadata.url} />
        <link rel="icon" type="image/x-icon" href={zeitLogo} />

        {/* Open Graph / Facebook */}
        <meta property="og:type" content="website" />
        <meta property="og:url" content={seoMetadata.url} />
        <meta property="og:site_name" content="ZeitTab" />
        <meta property="og:title" content={seoMetadata.title} />
        <meta property="og:description" content={seoMetadata.description} />
        <meta property="og:image" content={seoMetadata.ogImage} />
        <meta property="og:image:width" content={seoMetadata.imageWidth} />
        <meta property="og:image:height" content={seoMetadata.imageHeight} />
        <meta property="og:image:type" content={seoMetadata.imageType} />
        <meta property="og:locale" content="en_US" />

        {/* Twitter */}
        <meta name="twitter:card" content="summary" />
        <meta name="twitter:url" content={seoMetadata.url} />
        <meta name="twitter:title" content={seoMetadata.title} />
        <meta name="twitter:description" content={seoMetadata.description} />
        <meta name="twitter:image" content={seoMetadata.ogImage} />

        {/* Structured Data / JSON-LD */}
        <script type="application/ld+json">
          {`
            {
              "@context": "https://schema.org",
              "@type": "SoftwareApplication",
              "name": "ZeitTab",
              "applicationCategory": "BrowserExtension",
              "operatingSystem": "Chrome, Firefox",
              "description": "${seoMetadata.description}",
              "offers": {
                "@type": "Offer",
                "price": "0",
                "priceCurrency": "USD"
              },
              "image": "${seoMetadata.ogImage}",
              "url": "${seoMetadata.url}",
              "author": {
                "@type": "Person",
                "name": "Raj Shekhar",
                "url": "https://rshekhar.com"
              }
            }
          `}
        </script>
      </Helmet>

      <div
        className="min-h-screen flex flex-col relative overflow-hidden"
        style={{
          backgroundColor: '#0a0a0a',
          // Subtle radial gradient for depth
          backgroundImage: 'radial-gradient(ellipse at 50% 0%, rgba(201, 169, 98, 0.03) 0%, transparent 50%)',
        }}
      >
        {/* Grain texture overlay */}
        <div
          className="fixed inset-0 pointer-events-none opacity-[0.015]"
          style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noise'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noise)'/%3E%3C/svg%3E")`,
          }}
        />

        {/* Header */}
        <header className="relative z-20 px-6 md:px-12 py-6 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src={zeitLogo} alt="ZeitTab" className="w-8 h-8" />
            <span
              className="text-lg tracking-wide"
              style={{
                color: '#f5f0e8',
                fontFamily: "'Cormorant Garamond', serif",
                fontWeight: 500,
              }}
            >
              ZeitTab
            </span>
          </div>
          <a
            href="https://mokachika.com"
            className="text-sm tracking-wide transition-colors duration-300"
            style={{
              color: '#6a6a6a',
              fontFamily: "'Cormorant Garamond', serif",
            }}
            onMouseEnter={(e) => e.currentTarget.style.color = '#c9a962'}
            onMouseLeave={(e) => e.currentTarget.style.color = '#6a6a6a'}
          >
            mokachika
          </a>
        </header>

        {/* Main Hero */}
        <main className="flex-1 flex flex-col items-center justify-center relative z-10 px-6 md:px-12 -mt-16">
          {/* Clock Container */}
          <div className="relative mb-12">
            {/* Subtle glow behind clock */}
            <div
              className="absolute inset-0 blur-3xl opacity-20"
              style={{
                background: 'radial-gradient(circle, rgba(201, 169, 98, 0.3) 0%, transparent 70%)',
                transform: 'scale(1.5)',
              }}
            />

            {/* The Clock */}
            <div
              className="relative transition-opacity duration-400"
              style={{
                opacity: isTransitioning ? 0 : 1,
                // CSS custom properties for clock theming
                ['--clock-border-color' as string]: '#3a3a3a',
                ['--tick-hour-color' as string]: '#c9a962',
                ['--tick-minute-color' as string]: '#4a4a4a',
                ['--dot-hour-color' as string]: '#c9a962',
                ['--center-dot-color' as string]: '#c9a962',
                ['--bg-secondary' as string]: '#1a1a1a',
                ['--accent-color' as string]: '#c9a962',
              }}
            >
              <AnalogClock
                size={320}
                style={currentStyle.style}
                showSecondsHand={true}
                smoothSeconds={true}
                hourHandColor="#f5f0e8"
                minuteHandColor="#f5f0e8"
                secondHandColor="#c9a962"
                hourHandLength={35}
                minuteHandLength={50}
                secondHandLength={45}
              />
            </div>
          </div>

          {/* Style indicator */}
          <div className="flex items-center gap-2 mb-8">
            {CLOCK_STYLES.map((_, index) => (
              <button
                key={index}
                onClick={() => {
                  setIsTransitioning(true);
                  setTimeout(() => {
                    setCurrentStyleIndex(index);
                    setIsTransitioning(false);
                  }, 400);
                }}
                className="w-1.5 h-1.5 rounded-full transition-all duration-300"
                style={{
                  backgroundColor: index === currentStyleIndex ? '#c9a962' : '#3a3a3a',
                  transform: index === currentStyleIndex ? 'scale(1.5)' : 'scale(1)',
                }}
                aria-label={`View ${CLOCK_STYLES[index].name} style`}
              />
            ))}
          </div>

          {/* Typography */}
          <div className="text-center max-w-xl">
            <p
              className="text-xs tracking-[0.3em] uppercase mb-4"
              style={{ color: '#c9a962' }}
            >
              New Tab Extension
            </p>
            <h1
              className="text-4xl md:text-5xl lg:text-6xl mb-4 leading-tight"
              style={{
                color: '#f5f0e8',
                fontFamily: "'Cormorant Garamond', serif",
                fontWeight: 300,
                letterSpacing: '-0.02em',
              }}
            >
              A moment of calm,<br />every new tab
            </h1>
            <p
              className="text-base md:text-lg mb-10 leading-relaxed"
              style={{
                color: '#8a8a8a',
                fontFamily: "'Cormorant Garamond', serif",
                fontWeight: 400,
              }}
            >
              9 analog styles. 6 themes. Deep customization.<br />
              Time, date, week. Beautifully displayed.
            </p>

            {/* Download Buttons */}
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <a
                href={import.meta.env.VITE_CHROME_EXTENSION_URL || "https://chrome.google.com/webstore"}
                target="_blank"
                rel="noopener noreferrer"
                className="group flex items-center gap-3 px-6 py-3 rounded-full transition-all duration-300"
                style={{
                  backgroundColor: '#f5f0e8',
                  color: '#0a0a0a',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = '#ffffff';
                  e.currentTarget.style.transform = 'translateY(-2px)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = '#f5f0e8';
                  e.currentTarget.style.transform = 'translateY(0)';
                }}
              >
                <ChromeIcon />
                <span className="font-medium">Chrome</span>
              </a>
              <a
                href="https://addons.mozilla.org/en-US/firefox/addon/zeittab/"
                target="_blank"
                rel="noopener noreferrer"
                className="group flex items-center gap-3 px-6 py-3 rounded-full transition-all duration-300"
                style={{
                  backgroundColor: 'transparent',
                  color: '#f5f0e8',
                  border: '1px solid #3a3a3a',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.borderColor = '#c9a962';
                  e.currentTarget.style.transform = 'translateY(-2px)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.borderColor = '#3a3a3a';
                  e.currentTarget.style.transform = 'translateY(0)';
                }}
              >
                <FirefoxIcon />
                <span className="font-medium">Firefox</span>
              </a>
            </div>
          </div>
        </main>

        {/* Footer */}
        <footer className="relative z-10 px-6 md:px-12 py-8 flex flex-col sm:flex-row items-center justify-center gap-2 sm:gap-6 text-sm">
          <a
            href="https://github.com/rsh3khar/ZeitTab"
            target="_blank"
            rel="noopener noreferrer"
            className="transition-colors duration-300"
            style={{ color: '#4a4a4a' }}
            onMouseEnter={(e) => e.currentTarget.style.color = '#c9a962'}
            onMouseLeave={(e) => e.currentTarget.style.color = '#4a4a4a'}
          >
            Free & Open Source
          </a>
          <span style={{ color: '#2a2a2a' }} className="hidden sm:inline">•</span>
          <span style={{ color: '#4a4a4a' }}>9+ Clock Styles</span>
          <span style={{ color: '#2a2a2a' }} className="hidden sm:inline">•</span>
          <a
            href="https://rshekhar.com"
            target="_blank"
            rel="noopener noreferrer"
            className="transition-colors duration-300"
            style={{ color: '#4a4a4a' }}
            onMouseEnter={(e) => e.currentTarget.style.color = '#c9a962'}
            onMouseLeave={(e) => e.currentTarget.style.color = '#4a4a4a'}
          >
            by <span className="font-medium">Raj Shekhar</span>
          </a>
        </footer>
      </div>
    </>
  );
};
